import { z } from "zod";
import { generalConditions } from "./proposals/plan";

/**
 * Retail pricing and proposals (spec §8): a roof is priced from squares × the product's price per
 * square plus add-ons; remodel, repairs, and commercial are line items. The office sees expected
 * cost and margin (from per-unit costs in the price list) and a warning under the minimum margin.
 * Quantities are thousandths (32.5 SQ = 32500), amounts cents.
 */

export interface RetailProduct {
  key: string;
  name: string;
  /** Price and cost per square, cents. A square is a number of bundles (below), so ridge and starter bundles count too. */
  priceCents: number;
  costCents: number;
  bundlesPerSquare: number;
  /** Linear feet one bundle of the matching hip and ridge cap covers, and of starter strip. */
  ridgeLfPerBundle: number;
  starterLfPerBundle: number;
  /** What the customer reads about the shingle and system on the proposal. */
  description: string;
}

export interface RetailAddOn {
  key: string;
  name: string;
  unit: "SQ" | "EA" | "LF";
  priceCents: number;
  costCents: number;
  /**
   * SQ add-ons only: true when the rep enters the measured squares (no waste) and the price grows
   * with the roof's material squares, as steep and two-story fees do; false for squares entered as
   * they are (a full redeck covers the actual roof surface).
   */
  scaled: boolean;
  /** What the customer reads about it on the proposal when it's included (empty: nothing). */
  description: string;
}

export interface RetailPriceList {
  products: RetailProduct[];
  addOns: RetailAddOn[];
  /** Below this margin (whole percent), the proposal warns before sending. */
  minMarginPercent: number;
  /** How we install a roof, step by step (each new roof proposal starts with these; the rep can edit them). */
  roofSteps: string[];
  /** The closing paragraph on a roof proposal (credentials and warranty). {years} is the workmanship warranty. */
  roofClosing: string;
}

const item = z.object({ key: z.string().trim().min(1).max(40), name: z.string().trim().min(1).max(120), priceCents: z.number().int().min(0).max(10_000_000), costCents: z.number().int().min(0).max(10_000_000), description: z.string().trim().max(3000).default("") });
export const retailPriceListSchema = z.object({
  products: z.array(
    item.extend({
      bundlesPerSquare: z.number().int().min(1).max(10).default(3),
      ridgeLfPerBundle: z.number().int().min(1).max(500).default(30),
      starterLfPerBundle: z.number().int().min(1).max(500).default(100),
    }),
  ).max(50),
  addOns: z.array(item.extend({ unit: z.enum(["SQ", "EA", "LF"]), scaled: z.boolean().default(true) })).max(100),
  minMarginPercent: z.number().int().min(0).max(90),
  roofSteps: z.array(z.string().trim().min(1).max(1000)).max(40).default(() => [...DEFAULT_ROOF_STEPS]),
  roofClosing: z.string().trim().max(3000).default(() => DEFAULT_ROOF_CLOSING),
});

/** How we install a roof (Settings → Retail pricing; each proposal can change its own copy). */
export const DEFAULT_ROOF_STEPS: readonly string[] = [
  "Protect your property: before work starts, we set up our custom-built Catch-All net protection system around the house to shield landscaping, siding, windows, and outdoor areas from falling debris as much as possible.",
  "Tear off the existing roofing down to the decking, and haul away all old materials and debris.",
  "Inspect the decking sheet by sheet for rot, soft spots, and delamination. Damaged decking is replaced (or the roof is prepared for the new decking when a redeck is included), so the new roof is fastened to a solid, flat surface.",
  "Install ice and water shield, a self-sealing waterproof membrane, in the valleys, along walls and flashings, and around roof penetrations as needed: the places leaks most often start.",
  "Install synthetic underlayment over the rest of the roof. It's stronger and more water-resistant than felt paper: a second line of defense under the shingles.",
  "Install new drip edge where required, to steer water into the gutters and protect the edges of the decking and fascia.",
  "Replace flashings as required at walls, chimneys, and transitions, and install new pipe boot flashings at every plumbing vent.",
  "Ventilate the attic: install roof ventilation sized to your attic to meet the manufacturer's requirements. Good ventilation helps the shingles last, lowers attic heat, and reduces moisture problems.",
  "Install the starter strip, the new shingles to the manufacturer's specifications, and matching hip and ridge cap shingles.",
  "Paint all roof penetrations (vents, pipes, and flashings) to match the new roof for a clean, finished look.",
  "Clean up every day, run a magnetic sweep of the yard and driveway for nails, and do a final walk-through with you when the job is done.",
];

export const DEFAULT_ROOF_CLOSING =
  "NMT Construction is an Owens Corning Preferred Contractor, a credential held by a select group of independent roofing contractors who meet Owens Corning's standards for licensing, insurance, and reputation. Your new roof is installed as a complete Owens Corning roofing system and is backed by our industry-leading {years}-year workmanship warranty, in addition to Owens Corning's manufacturer warranty on the materials.";

const DECKING_TEXT =
  "Decking is the foundation of your roof: every shingle and nail depends on it. Over time, decking can dry out, delaminate, or rot, and older plank decking leaves gaps that can't hold nails well. New 7/16\" OSB gives your new roof a solid, flat, dry surface that holds every nail for its full wind rating, removes hidden damage before it becomes a leak, and supports the full manufacturer's warranty. It's an investment in your home that protects the new roof for its whole life and adds value when you sell.";

/** Starting prices (edit in Settings → Retail pricing). Costs are rough and need Ryan's numbers. */
export const DEFAULT_RETAIL_PRICE_LIST: RetailPriceList = {
  products: [
    {
      key: "duration",
      name: "Owens Corning TruDefinition Duration (Class 3)",
      priceCents: 52_500,
      costCents: 30_000,
      bundlesPerSquare: 3,
      ridgeLfPerBundle: 30,
      starterLfPerBundle: 100,
      description:
        "Owens Corning TruDefinition® Duration® shingles are a UL 2218 Class 3 impact-resistant shingle that combines a bold, dimensional look with patented SureNail® Technology: a tough, woven engineered strip in the nailing zone that gives outstanding grip and wind resistance. TruDefinition® colors add depth and curb appeal, with StreakGuard™ algae resistance and Owens Corning's Limited Lifetime warranty (for as long as you own your home).",
    },
    {
      key: "flex",
      name: "Owens Corning Duration FLEX (Class 4 impact)",
      priceCents: 62_500,
      costCents: 37_500,
      bundlesPerSquare: 3,
      ridgeLfPerBundle: 30,
      starterLfPerBundle: 100,
      description:
        "Owens Corning Duration FLEX® shingles are made with SBS polymer-modified asphalt for flexibility and toughness, and carry a UL 2218 Class 4 impact rating, the highest rating for impact resistance. Class 4 shingles may qualify you for a discount on your homeowner's insurance (ask your agent). They include SureNail® Technology for wind resistance and Owens Corning's Limited Lifetime warranty.",
    },
    {
      key: "oakridge",
      name: "Owens Corning Oakridge",
      priceCents: 47_500,
      costCents: 27_500,
      bundlesPerSquare: 3,
      ridgeLfPerBundle: 30,
      starterLfPerBundle: 100,
      description:
        "Owens Corning Oakridge® shingles are a dependable, attractive architectural (laminated) shingle with a dimensional look, StreakGuard™ algae resistance, and Owens Corning's Limited Lifetime warranty.",
    },
  ],
  addOns: [
    { key: "steep8", name: "Steep roof 8/12 to 9/12", unit: "SQ", priceCents: 2_500, costCents: 1_200, scaled: true, description: "" },
    { key: "steep10", name: "Steep roof 10/12 and up", unit: "SQ", priceCents: 5_000, costCents: 3_000, scaled: true, description: "" },
    { key: "twostory", name: "Two-story", unit: "SQ", priceCents: 2_500, costCents: 2_000, scaled: true, description: "" },
    { key: "layer", name: "Extra layer tear-off", unit: "SQ", priceCents: 2_500, costCents: 1_200, scaled: true, description: "" },
    { key: "redeck", name: "Full redeck with new 7/16\" OSB roof decking", unit: "SQ", priceCents: 20_000, costCents: 11_000, scaled: false, description: DECKING_TEXT },
    { key: "decking", name: "Decking replacement (sheet)", unit: "EA", priceCents: 9_500, costCents: 5_000, scaled: false, description: "" },
    { key: "ridgevent", name: "Ridge vent", unit: "LF", priceCents: 1_000, costCents: 450, scaled: false, description: "" },
  ],
  minMarginPercent: 30,
  roofSteps: [...DEFAULT_ROOF_STEPS],
  roofClosing: DEFAULT_ROOF_CLOSING,
};

/** A product's or add-on's customer text: its own, else the default for its key (lists saved before descriptions existed). */
export function describedAs(kind: "product" | "addOn", key: string, own: string): string {
  if (own.trim()) return own.trim();
  const d = kind === "product" ? DEFAULT_RETAIL_PRICE_LIST.products.find((p) => p.key === key) : DEFAULT_RETAIL_PRICE_LIST.addOns.find((a) => a.key === key);
  return d?.description ?? "";
}

/** x × cents ÷ 1000, rounded half up (whole-number math). */
function times(thousandths: number, cents: number) {
  const p = thousandths * cents;
  return Math.floor(p / 1000) + (p % 1000 >= 500 ? 1 : 0);
}

export interface RoofInput {
  /** Measured roof area, thousandths of a square, without waste (from the report). */
  squares: number;
  /** Waste (whole percent), added to the field shingles. */
  wastePercent: number;
  /** Hips + ridges and eaves + rakes, whole feet: ridge cap and starter bundles. */
  ridgeLf: number;
  starterLf: number;
  productKey: string;
  addOns: { key: string; quantity: number }[];
}

export interface PricedLine {
  description: string;
  amountCents: number;
  costCents: number;
  /** For the customer, when quantities or unit prices are shown: "12.67", "SQ", and the price each. */
  quantity?: number;
  unit?: string;
  unitPriceCents?: number;
  /** The price list item it came from (for its description on the proposal). */
  addOnKey?: string;
  /** Shown under "Upgrades" on the proposal (still part of the price). */
  upgrade?: boolean;
}

/** Whole bundles for the roof: field shingles with waste, ridge cap, and starter, each rounded up. */
export function roofBundles(r: Pick<RoofInput, "squares" | "wastePercent" | "ridgeLf" | "starterLf">, p: Pick<RetailProduct, "bundlesPerSquare" | "ridgeLfPerBundle" | "starterLfPerBundle">) {
  const ceilDiv = (a: number, b: number) => (a <= 0 ? 0 : Math.floor((a + b - 1) / b));
  const field = ceilDiv(r.squares * (100 + Math.max(0, r.wastePercent)) * p.bundlesPerSquare, 100_000);
  const ridge = ceilDiv(Math.max(0, r.ridgeLf), p.ridgeLfPerBundle);
  const starter = ceilDiv(Math.max(0, r.starterLf), p.starterLfPerBundle);
  const total = field + ridge + starter;
  /** Thousandths of a square (38 bundles ÷ 3 = 12.667). */
  const materialSquares = Math.floor((total * 1000 * 2 + p.bundlesPerSquare) / (p.bundlesPerSquare * 2));
  return { field, ridge, starter, total, materialSquares };
}

/** total × cents ÷ per, rounded half up (whole-number math). */
function share(total: number, cents: number, per: number) {
  const n = total * cents;
  return Math.floor(n / per) + ((n % per) * 2 >= per ? 1 : 0);
}

/**
 * The roof as priced lines. The system is priced by the bundles it takes (field with waste, ridge
 * cap, starter) at the price per square ÷ bundles per square. Scaled square add-ons (steep,
 * two-story...) are entered as measured squares and grow by the same ratio the roof did.
 */
export function priceRoof(r: RoofInput, list: RetailPriceList): PricedLine[] {
  const p = list.products.find((x) => x.key === r.productKey);
  if (!p) throw new Error("Pick a shingle.");
  if (!Number.isInteger(r.squares) || r.squares <= 0) throw new Error("Enter the squares.");
  const b = roofBundles(r, p);
  const lines: PricedLine[] = [
    {
      description: `${p.name} roofing system: complete tear-off and installation`,
      amountCents: share(b.total, p.priceCents, p.bundlesPerSquare),
      costCents: share(b.total, p.costCents, p.bundlesPerSquare),
      quantity: b.materialSquares,
      unit: "SQ",
      unitPriceCents: p.priceCents,
    },
  ];
  for (const a of r.addOns) {
    const def = list.addOns.find((x) => x.key === a.key);
    if (!def || !a.quantity) continue;
    if (!Number.isInteger(a.quantity) || a.quantity < 0) throw new Error("Quantities are whole thousandths.");
    // Steep and the like: the measured squares × (material squares ÷ measured squares).
    const qty = def.unit === "SQ" && def.scaled ? share(a.quantity, b.materialSquares, r.squares) : a.quantity;
    lines.push({ description: def.name, amountCents: times(qty, def.priceCents), costCents: times(qty, def.costCents), quantity: qty, unit: def.unit, unitPriceCents: def.priceCents, addOnKey: def.key });
  }
  return lines;
}

export interface RetailTotals {
  totalCents: number;
  costCents: number;
  marginCents: number;
  /** Whole percent of the price; null when the total is 0. */
  marginPercent: number | null;
  belowMinimum: boolean;
  depositCents: number;
}

/** Totals for all lines (custom lines have no known cost), less any discount; the deposit is a whole percent, rounded half up. */
export function retailTotals(lines: { amountCents: number; costCents?: number | null }[], discountCents: number, depositPercent: number, minMarginPercent: number): RetailTotals {
  const gross = lines.reduce((s, l) => s + l.amountCents, 0);
  const totalCents = Math.max(0, gross - Math.max(0, discountCents));
  const costCents = lines.reduce((s, l) => s + (l.costCents ?? 0), 0);
  const marginCents = totalCents - costCents;
  const marginPercent = totalCents ? Math.round((marginCents / totalCents) * 100) : null;
  const dep = totalCents * Math.min(100, Math.max(0, depositPercent));
  const depositCents = Math.floor(dep / 100) + (dep % 100 >= 50 ? 1 : 0);
  return { totalCents, costCents, marginCents, marginPercent, belowMinimum: marginPercent !== null && marginPercent < minMarginPercent, depositCents };
}

export const RETAIL_VARIANTS = ["retail_roof", "remodel", "repairs", "commercial"] as const;
export type RetailVariant = (typeof RETAIL_VARIANTS)[number];
export const RETAIL_VARIANT_LABELS: Record<RetailVariant, string> = { retail_roof: "Retail Roof", remodel: "Remodel", repairs: "Repairs", commercial: "Commercial" };

/** What the rep fills in for a retail proposal. */
export interface RetailForm {
  variant: RetailVariant;
  title: string;
  scope: string;
  roof: RoofInput | null;
  /** Extra lines (remodel, repairs, anything not on the price list). Cost is optional. */
  lines: { description: string; amountCents: number; costCents: number | null; upgrade?: boolean }[];
  discountCents: number;
  depositPercent: number;
  notes: string;
  signerName: string;
  signerEmail: string;
  /** What the customer sees on the price section (all off: the items and the total only). */
  show: RetailShow;
  /** How we'll install the roof, as the customer reads it (from the price list's steps; editable). */
  steps: string[];
}

export interface RetailShow {
  quantities: boolean;
  unitPrices: boolean;
  linePrices: boolean;
}
const NO_SHOW: RetailShow = { quantities: false, unitPrices: false, linePrices: false };

export const retailFormSchema = z.object({
  variant: z.enum(RETAIL_VARIANTS),
  title: z.string().trim().max(120),
  scope: z.string().trim().max(10_000),
  roof: z
    .object({
      squares: z.number().int().min(0).max(10_000_000),
      wastePercent: z.number().int().min(0).max(100).default(0),
      ridgeLf: z.number().int().min(0).max(1_000_000).default(0),
      starterLf: z.number().int().min(0).max(1_000_000).default(0),
      productKey: z.string().max(40),
      addOns: z.array(z.object({ key: z.string().max(40), quantity: z.number().int().min(0).max(10_000_000) })).max(50),
    })
    .nullable(),
  lines: z.array(z.object({ description: z.string().trim().min(1).max(300), amountCents: z.number().int().min(0).max(100_000_000), costCents: z.number().int().min(0).max(100_000_000).nullable(), upgrade: z.boolean().default(false) })).max(100),
  discountCents: z.number().int().min(0).max(100_000_000),
  depositPercent: z.number().int().min(0).max(100),
  notes: z.string().trim().max(4000),
  signerName: z.string().trim().max(160),
  signerEmail: z.string().trim().max(254),
  show: z.object({ quantities: z.boolean(), unitPrices: z.boolean(), linePrices: z.boolean() }).default(NO_SHOW),
  // Blank lines are kept while the rep types; they're left off the proposal.
  steps: z.array(z.string().max(1000)).max(60).default([]),
});

/** A saved form, filled out with what forms saved before these fields existed would have had. */
export function retailForm(f: RetailForm, list: RetailPriceList): RetailForm {
  return {
    ...f,
    roof: f.roof ? { ...f.roof, wastePercent: f.roof.wastePercent ?? 0, ridgeLf: f.roof.ridgeLf ?? 0, starterLf: f.roof.starterLf ?? 0 } : null,
    show: f.show ?? NO_SHOW,
    steps: f.steps ?? (f.roof ? [...list.roofSteps] : []),
  };
}

/** All priced lines for a form: the roof's, then the extra ones. */
export function retailLines(form: RetailForm, list: RetailPriceList): PricedLine[] {
  const f = retailForm(form, list);
  return [...(f.roof?.squares ? priceRoof(f.roof, list) : []), ...f.lines.map((l) => ({ description: l.description, amountCents: l.amountCents, costCents: l.costCents ?? 0, ...(l.upgrade ? { upgrade: true } : {}) }))];
}

/** A starting form for a job. */
export function newRetailForm(variant: RetailVariant, signerName: string, signerEmail: string, list: RetailPriceList): RetailForm {
  return {
    variant,
    title: variant === "retail_roof" ? "Roof replacement" : RETAIL_VARIANT_LABELS[variant],
    scope: "",
    roof: variant === "retail_roof" ? { squares: 0, wastePercent: 10, ridgeLf: 0, starterLf: 0, productKey: list.products[0]?.key ?? "", addOns: [] } : null,
    lines: [],
    discountCents: 0,
    depositPercent: 0,
    notes: "",
    signerName,
    signerEmail,
    show: NO_SHOW,
    steps: variant === "retail_roof" ? [...list.roofSteps] : [],
  };
}

/** A retail proposal as sent (frozen): what the PDF and the customer's page show. Costs are left out. */
export interface RetailSnapshot {
  variant: RetailVariant;
  title: string;
  scope: string;
  /** Unit and quantity are there for lines from the price list. */
  lines: { description: string; amountCents: number; quantity?: number; unit?: string; unitPriceCents?: number; upgrade?: boolean }[];
  /** What's shown on the price section (missing on proposals sent before: everything was). */
  show?: RetailShow;
  /** The written-out scope: the system, how we'll install it, what's included, and why NMT. */
  sections?: { heading: string; paragraphs: string[]; steps?: string[] }[];
  discountCents: number;
  totalCents: number;
  depositPercent: number;
  depositCents: number;
  notes: string;
  customer: { name: string; firstName: string; address: string; cityStateZip: string; phone: string; email: string };
  preparedBy: { name: string; phone: string; email: string };
  /** "October 17, 2026". */
  date: string;
  validDays: number;
  /** Lines the customer fills in when signing (a roof asks for colors). */
  selections: string[];
}

/** Why a retail proposal can't be sent yet (empty when it can). The margin is a warning, not a blocker. */
export function retailProblems(f: RetailForm, lines: PricedLine[], totals: RetailTotals): string[] {
  const out: string[] = [];
  if (!lines.length) out.push(f.variant === "retail_roof" ? "Enter the squares or add a line." : "Add at least one line.");
  if (lines.length && totals.totalCents <= 0) out.push("The total has to be more than $0.");
  if (!f.scope.trim() && f.variant !== "retail_roof") out.push("Describe the scope of work.");
  return out;
}

/** The color lines a roof proposal asks the customer to fill in when signing. */
export const RETAIL_ROOF_SELECTIONS = ["Shingle color", "Drip edge / metal color"];

/**
 * The General Conditions for a retail proposal: the insurance proposal's, without the insurance
 * supplements paragraph (and the first paragraph's pointer to it). Same warranty, change order,
 * and payment terms.
 */
export function retailGeneralConditions(validDays: number): { title?: string; body: string }[] {
  return generalConditions(validDays)
    .filter((p) => p.title !== "Insurance Supplements")
    .map((p, i) => (i === 0 ? { ...p, body: p.body.replace(", except insurance supplements as described under \u201CInsurance Supplements\u201D below", "") } : p));
}

/**
 * The scope written out for the customer, for a roof: the shingle system, how we'll install it,
 * anything included with its own explanation (a redeck), and why NMT (credentials and warranty).
 */
export function retailSections(form: RetailForm, list: RetailPriceList, lines: PricedLine[], warrantyYears: number): NonNullable<RetailSnapshot["sections"]> {
  const f = retailForm(form, list);
  if (!f.roof) return [];
  const out: NonNullable<RetailSnapshot["sections"]> = [];
  const p = list.products.find((x) => x.key === f.roof!.productKey);
  const system = p ? describedAs("product", p.key, p.description) : "";
  if (system) out.push({ heading: "Your new roofing system", paragraphs: [system] });
  const steps = f.steps.map((x) => x.trim()).filter(Boolean);
  if (steps.length) out.push({ heading: "How we'll install it", paragraphs: [], steps });
  for (const l of lines) {
    const a = l.addOnKey ? list.addOns.find((x) => x.key === l.addOnKey) : null;
    const text = a ? describedAs("addOn", a.key, a.description) : "";
    if (a && text) out.push({ heading: a.name, paragraphs: [text] });
  }
  const closing = list.roofClosing.trim().replaceAll("{years}", String(warrantyYears));
  if (closing) out.push({ heading: "Why NMT Construction", paragraphs: [closing] });
  return out;
}
